'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, DropdownMenu, Input } from '@common/components';
import { SvgChevronup } from '@common/icon';
import { cn } from '@core/utils';
import { useState } from 'react';
import { CustomLink } from '@/app/_components/CustomLink';
import { ROUTES } from '@/constants';
import type { HouseholdSize, MedianIncomeState } from '../_hooks/useMedianIncomeState';
import { HOUSEHOLD_OPTIONS } from '../constants';
interface MobileStickyCalculatorProps {
  state: MedianIncomeState;
  /** 상세 페이지 등에서 promo 용도로 쓸 때 하단 "내 중위소득 확인하기 →" CTA 노출. */
  showNavCta?: boolean;
}

/**
 * 모바일/태블릿 하단 sticky 계산기 — 헤더 toggle 로 펼침/접힘. 초기 펼침.
 * PC에서는 우측 사이드바의 `<Calculator />`가 같은 state 를 공유한다.
 */
export const MobileStickyCalculator = t0 => {
  const $ = _c(44);
  const {
    state,
    showNavCta
  } = t0;
  const [isOpen, setIsOpen] = useState(true);
  const {
    householdSize,
    amountInput,
    percentInput,
    setHouseholdSize,
    onAmountChange,
    onPercentChange
  } = state;
  const householdLabel = HOUSEHOLD_OPTIONS[householdSize - 1]?.label ?? `${householdSize}인`;
  const summary = `${householdLabel} · ${amountInput || "0"}만원 · ${percentInput || "0"}%`;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setIsOpen(_temp);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== summary) {
    t2 = <span className="truncate font-700 text-Gray-900 typo-body4">{summary}</span>;
    $[1] = summary;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = isOpen && "rotate-180";
  let t4;
  if ($[3] !== t3) {
    t4 = cn("size-16 text-Gray-700 transition-transform duration-200", t3);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = <SvgChevronup aria-hidden="true" className={t4} />;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== isOpen || $[8] !== t2 || $[9] !== t5) {
    t6 = <button type="button" aria-expanded={isOpen} onClick={t1} className="flex w-full cursor-pointer items-center justify-between gap-12 bg-transparent px-16 py-12 text-left">{t2}{t5}</button>;
    $[7] = isOpen;
    $[8] = t2;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]";
  let t8;
  if ($[11] !== t7) {
    t8 = cn("grid transition-[grid-template-rows] duration-300", t7);
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = HOUSEHOLD_OPTIONS.map(_temp2);
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== setHouseholdSize) {
    t10 = v => setHouseholdSize(v as HouseholdSize);
    $[14] = setHouseholdSize;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== householdSize || $[17] !== t10) {
    t11 = <Row label={"\uAC00\uAD6C\uC6D0"} htmlFor="mobile-calc-hh"><DropdownMenu dimensions="H40" options={t9} value={householdSize} onChange={t10} placeholder={"\uAC00\uAD6C\uC6D0 \uC218 \uC120\uD0DD"} /></Row>;
    $[16] = householdSize;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== onAmountChange) {
    t12 = e => onAmountChange(e.target.value);
    $[19] = onAmountChange;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="font-400 text-Gray-600 typo-caption1">만원</span>;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== amountInput || $[23] !== t12) {
    t14 = <Row label={"\uAC00\uAD6C\uC6D0 \uC6D4\uC18C\uB4DD"} htmlFor="mobile-calc-amount"><Input id="mobile-calc-amount" type="local-string-number" inputMode="numeric" dimensions="H40" value={amountInput} onChange={t12} placeholder={"\uC608: 300"} rightContent={t13} /></Row>;
    $[22] = amountInput;
    $[23] = t12;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== onPercentChange) {
    t15 = e_0 => onPercentChange(e_0.target.value);
    $[25] = onPercentChange;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span className="font-400 text-Gray-600 typo-caption1">%</span>;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== percentInput || $[29] !== t15) {
    t17 = <Row label={"\uC911\uC704\uC18C\uB4DD \uBE44\uC728"} htmlFor="mobile-calc-pct"><Input id="mobile-calc-pct" type="text" inputMode="decimal" dimensions="H40" value={percentInput} onChange={t15} placeholder={"\uC608: 100"} rightContent={t16} /></Row>;
    $[28] = percentInput;
    $[29] = t15;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== showNavCta) {
    t18 = showNavCta ? <Button asChild={true} dimensions="H40" className="mt-4 w-full"><CustomLink href={ROUTES.MEDIAN_INCOME.pathname}>내 중위소득 확인하기 →</CustomLink></Button> : null;
    $[31] = showNavCta;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t11 || $[34] !== t14 || $[35] !== t17 || $[36] !== t18) {
    t19 = <div className="overflow-hidden"><div className="flex flex-col gap-8 px-16 pb-12">{t11}{t14}{t17}{t18}</div></div>;
    $[33] = t11;
    $[34] = t14;
    $[35] = t17;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== t19 || $[39] !== t8) {
    t20 = <div className={t8}>{t19}</div>;
    $[38] = t19;
    $[39] = t8;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t20 || $[42] !== t6) {
    t21 = <div className="fixed inset-x-0 bottom-[calc(70px+var(--safe-area-bottom,0px))] z-20 hidden border-t-1 border-Gray-200 bg-Gray-white mobile-tablet:block">{t6}{t20}</div>;
    $[41] = t20;
    $[42] = t6;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  return t21;
};
interface RowProps {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}
const Row = t0 => {
  const $ = _c(8);
  const {
    label,
    htmlFor,
    children
  } = t0;
  let t1;
  if ($[0] !== htmlFor || $[1] !== label) {
    t1 = <label htmlFor={htmlFor} className="w-80 shrink-0 text-Gray-700 typo-caption1">{label}</label>;
    $[0] = htmlFor;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children) {
    t2 = <div className="flex-1">{children}</div>;
    $[3] = children;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className="flex items-center gap-12">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
function _temp(prev) {
  return !prev;
}
function _temp2(o) {
  return {
    value: o.value,
    label: o.label
  };
}